import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://heka.care';
const DEFAULT_OG = '/og-image.png';

interface SEOProps {
  /** Page title (will be appended with " — Heka.care" unless includeSiteName=false). */
  title: string;
  description: string;
  /** Canonical path (starts with /). Defaults to current URL if not supplied. */
  path?: string;
  /** Absolute URL or relative path. Defaults to /og-image.png. */
  image?: string;
  /** 'website' | 'article' | 'profile' etc. */
  type?: string;
  /** Omit the " — Heka.care" suffix for the home page only. */
  includeSiteName?: boolean;
  /** Additional structured-data blocks. Each is stringified into its own <script type="application/ld+json">. */
  jsonLd?: Array<Record<string, unknown>>;
  /** Set to true to block indexing. */
  noindex?: boolean;
  /** Optional keywords (comma-separated). Modern engines ignore; legacy ones still use. */
  keywords?: string;
  /** Article publish date in ISO 8601 for /blog posts (future). */
  publishedAt?: string;
  /** Author name for article-type pages. */
  author?: string;
}

/**
 * Per-route SEO. Renders into <head> via react-helmet-async.
 *
 * - Unique title + description per page (critical for SERP snippets)
 * - Canonical URL (prevents duplicate-content penalties from query strings)
 * - Open Graph + Twitter cards targeted at Ghana (locale en_GH)
 * - hreflang alternates for en-GH + Twi + Ga + Ewe (x-default → en)
 * - Optional JSON-LD blocks (FAQPage, BreadcrumbList, Service, MedicalWebPage, etc.)
 */
export default function SEO({
  title,
  description,
  path,
  image = DEFAULT_OG,
  type = 'website',
  includeSiteName = true,
  jsonLd,
  noindex,
  keywords,
  publishedAt,
  author,
}: SEOProps) {
  const fullTitle = includeSiteName ? `${title} — Heka.care` : title;
  const url = path ? `${SITE_URL}${path}` : SITE_URL;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {author && <meta name="author" content={author} />}

      {/* hreflang — targets Ghana and Ghanaian language speakers */}
      <link rel="alternate" hrefLang="en-GH" href={url} />
      <link rel="alternate" hrefLang="tw-GH" href={url} />
      <link rel="alternate" hrefLang="gaa-GH" href={url} />
      <link rel="alternate" hrefLang="ee-GH" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Heka.care" />
      <meta property="og:locale" content="en_GH" />
      <meta property="og:locale:alternate" content="tw_GH" />
      <meta property="og:locale:alternate" content="gaa_GH" />
      <meta property="og:locale:alternate" content="ee_GH" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Heka.care — Telehealth for Ghana" />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@hekacare" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content="Heka.care — Telehealth for Ghana" />

      {/* JSON-LD */}
      {(jsonLd ?? []).map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Shared JSON-LD blocks that belong on multiple pages (brand + WebSite search action).
 * Include these alongside the page-specific jsonLd array via [...commonJsonLd, ...pageSpecific].
 */
export const commonJsonLd = {
  organization: {
    '@context': 'https://schema.org',
    '@type': 'MedicalOrganization',
    name: 'Heka.care',
    legalName: 'Haven Technologies Ltd',
    url: 'https://heka.care',
    logo: 'https://heka.care/logo.png',
    image: 'https://heka.care/og-image.png',
    description: "Ghana's national telehealth platform — book doctors, order pharmacy deliveries, access NHIS benefits.",
    email: 'hello@heka.care',
    telephone: '+233-30-000-0000',
    foundingDate: '2025',
    founders: [{ '@type': 'Organization', name: 'Haven Technologies Ltd' }],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Accra',
      addressRegion: 'Greater Accra',
      addressCountry: 'GH',
      postalCode: 'CT 210',
    },
    areaServed: [
      { '@type': 'Country', name: 'Ghana' },
      { '@type': 'AdministrativeArea', name: 'Greater Accra Region' },
      { '@type': 'AdministrativeArea', name: 'Ashanti Region' },
      { '@type': 'AdministrativeArea', name: 'Western Region' },
      { '@type': 'AdministrativeArea', name: 'Central Region' },
    ],
    availableLanguage: ['en', 'tw', 'gaa', 'ee'],
    medicalSpecialty: [
      'GeneralPractice', 'Pediatrics', 'Obstetrics', 'Cardiology',
      'PrimaryCare', 'InternalMedicine', 'FamilyPractice',
    ],
    sameAs: [
      'https://x.com/hekacare',
      'https://www.linkedin.com/company/heka-care',
      'https://facebook.com/hekacare',
      'https://youtube.com/@hekacare',
    ],
  } as Record<string, unknown>,

  website: {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Heka.care',
    alternateName: 'Heka',
    url: 'https://heka.care',
    inLanguage: ['en-GH', 'tw-GH', 'gaa-GH', 'ee-GH'],
    publisher: { '@type': 'Organization', name: 'Haven Technologies Ltd' },
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://heka.care/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  } as Record<string, unknown>,
};

/**
 * Build a BreadcrumbList JSON-LD block from a list of `{name, path}` crumbs.
 * The last crumb represents the current page.
 */
export function breadcrumbs(items: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path}`,
    })),
  };
}
